// transcript_merge.ts — pure function: merge persisted transcript events into
// the running SessionStore via the same dispatch logic as live SSE.
//
// Why a separate file (not a store action):
//   - PR-PSO-04 single source of truth: store has ONE applySSEEvent reducer.
//     We don't introduce a second merge reducer; we replay through applySSEEvent.
//   - PR-PSO-03: this is a pure dispatch helper, no I/O, easy to unit-test.
//
// Idempotency: events with event_id ≤ store.last_event_id are skipped.
// applySSEEvent already takes Math.max(last_event_id, event.id), so even if
// callers pass duplicates, store last_event_id stays monotonic.
//
// 0 `: any`. 0 `// @ts-ignore`. 0 console.* in this file.

import type { TranscriptEvent } from '@/types/transcript';

/**
 * Minimal contract a SessionStore must satisfy to receive a transcript merge.
 * Lets us unit-test without mounting the full Zustand store; production code
 * passes the actual store instance.
 */
export interface TranscriptMergeTarget {
  current: { last_event_id: number };
  applySSEEvent: (event: { type: string; id: number; data: Record<string, unknown> }) => void;
}

/**
 * Merge persisted transcript events into the store.
 *
 * @returns count of events actually applied (skipped ones don't count).
 */
export function mergeTranscriptIntoStore(
  store: TranscriptMergeTarget,
  events: readonly TranscriptEvent[],
): number {
  if (!events || events.length === 0) return 0;
  const baseline = store.current.last_event_id;
  let applied = 0;
  // Sort defensively: contract guarantees ascending order, but a
  // network proxy or test fixture could shuffle.
  const ordered = [...events].sort((a, b) => a.event_id - b.event_id);
  for (const ev of ordered) {
    if (ev.event_id <= baseline) continue;
    store.applySSEEvent({
      type: ev.type,
      id: ev.event_id,
      data: ev.data,
    });
    applied += 1;
  }
  return applied;
}

/**
 * Compute the URL query suffix for SSE resume after a transcript merge.
 * Returns the integer to pass to last_event_id, which is max(events.event_id, baseline).
 */
export function nextSSEResumeId(
  baseline: number,
  events: readonly TranscriptEvent[],
): number {
  if (!events || events.length === 0) return baseline;
  let max = baseline;
  for (const ev of events) {
    if (ev.event_id > max) max = ev.event_id;
  }
  return max;
}
